/**
 * @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
 */

/**
 * @module list/listformatting/listitemitalicintegration
 */

import { Plugin, type PluginDependenciesOf } from '@ckeditor/ckeditor5-core';
import { env } from '@ckeditor/ckeditor5-utils';

import { ListEditing } from '../list/listediting.js';
import type { ListFormatting } from '../listformatting.js';

/**
 * The list item italic integration plugin.
 */
export class ListItemItalicIntegration extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'ListItemItalicIntegration' as const;
	}

	/**
	 * @inheritDoc
	 */
	public static override get isOfficialPlugin(): true {
		return true;
	}

	/**
	 * @inheritDoc
	 */
	public static get requires(): PluginDependenciesOf<[ ListEditing ]> {
		return [ ListEditing ];
	}

	/**
	 * @inheritDoc
	 */
	public init(): void {
		const editor = this.editor;
		const ListFormatting: ListFormatting = editor.plugins.get( 'ListFormatting' );
		const listEditing = editor.plugins.get( ListEditing );

		if ( !editor.plugins.has( 'ItalicEditing' ) || !this.editor.config.get( 'list.enableListItemMarkerFormatting' ) ) {
			return;
		}

		ListFormatting.registerFormatAttribute( 'italic', 'listItemItalic' );

		// Register the downcast strategy in init() so that the attribute name is registered before the list editing
		// registers its converters.
		// This ensures that the attribute is recognized by downcast strategies and bogus paragraphs are handled correctly.
		listEditing.registerDowncastStrategy( {
			scope: 'item',
			attributeName: 'listItemItalic',

			setAttributeOnDowncast( writer, value, viewElement, options ) {
				/* v8 ignore next -- Downcast callbacks are only registered for meaningful list marker formatting values. */
				if ( value ) {
					writer.addClass( 'ck-list-marker-italic', viewElement );

					// See: https://github.com/ckeditor/ckeditor5/issues/18790.
					if ( env.isSafari && !( options && options.dataPipeline ) ) {
						writer.setStyle( '--ck-content-list-marker-dummy-italic', '0', viewElement );
					}
				}
			}
		} );
	}

	/**
	 * @inheritDoc
	 */
	public afterInit(): void {
		const editor = this.editor;
		const model = editor.model;

		if ( !editor.plugins.has( 'ItalicEditing' ) || !this.editor.config.get( 'list.enableListItemMarkerFormatting' ) ) {
			return;
		}

		model.schema.extend( '$listItem', { allowAttributes: 'listItemItalic' } );
		model.schema.setAttributeProperties( 'listItemItalic', {
			isFormatting: true
		} );

		model.schema.addAttributeCheck( context => {
			const item = context.last;

			if ( !item.getAttribute( 'listItemId' ) ) {
				return false;
			}
		}, 'listItemItalic' );

		editor.conversion.for( 'upcast' ).attributeToAttribute( {
			model: 'listItemItalic',
			view: {
				name: 'li',
				classes: 'ck-list-marker-italic'
			}
		} );
	}
}
